import { lazy, Suspense, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CarFront, Route, Ticket, TrendingUp, UserCog, UserPlus, Users } from 'lucide-react'
import { env } from '@/lib/env'
import { formatRelative } from '@/lib/datetime'
import { formatNumber } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { CardSkeleton, Skeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { EmptyState, ErrorState } from '@/components/ui/States'
import { fetchDashboardStats, fetchRecentActivity } from './api'
import { KpiCard } from './KpiCard'
import { StatusBreakdown } from './StatusBreakdown'
import styles from './DashboardPage.module.css'

// The charting library is the largest dependency in the bundle and none of it
// is needed to render the figures above it, so it arrives on its own.
const TrendChart = lazy(() => import('./TrendChart').then((module) => ({ default: module.TrendChart })))

const TREND_DAYS = 14

function ChartFallback() {
  return <Skeleton height={220} radius="var(--radius-md)" />
}

export function DashboardPage() {
  const statsQuery = useQuery({
    queryKey: queryKeys.dashboard.stats(TREND_DAYS),
    queryFn: () => fetchDashboardStats(TREND_DAYS),
  })

  const activityQuery = useQuery({
    queryKey: queryKeys.dashboard.activity(10),
    queryFn: () => fetchRecentActivity(10),
  })

  useEffect(() => {
    document.title = `Overview · ${env.appName} Operations`
  }, [])

  const stats = statsQuery.data

  return (
    <>
      <PageHeader
        title="Operations overview"
        description="Live figures from the Drive For Me platform. Every number below is counted from the production database at the moment this page loaded."
        actions={
          <>
            <Button
              variant="secondary"
              size="md"
              loading={statsQuery.isFetching}
              onClick={() => void statsQuery.refetch()}
            >
              Refresh figures
            </Button>
            <Link to="/trip-requests?status=PENDING" className={styles.primaryLink}>
              Review approval queue
            </Link>
          </>
        }
      />

      {statsQuery.isError ? (
        <Card>
          <ErrorState error={statsQuery.error} onRetry={() => void statsQuery.refetch()} />
        </Card>
      ) : (
        <>
          <section className={styles.kpiGrid} aria-label="Key figures">
            {statsQuery.isLoading || !stats ? (
              Array.from({ length: 5 }).map((_, index) => <CardSkeleton key={index} height={148} />)
            ) : (
              <>
                <KpiCard
                  label="Awaiting approval"
                  value={stats.pendingTripRequests}
                  icon={Route}
                  emphasis
                  to="/trip-requests?status=PENDING"
                  hint={
                    stats.pendingTripRequests === 0
                      ? 'The queue is clear.'
                      : 'Trip requests waiting for a driver to be assigned.'
                  }
                />
                <KpiCard
                  label="Trips in progress"
                  value={stats.activeTrips}
                  icon={CarFront}
                  to="/trips?status=STARTED"
                  breakdown={[
                    { label: 'completed', value: stats.completedTrips, tone: 'success' },
                    { label: 'cancelled', value: stats.cancelledTrips, tone: 'danger' },
                  ]}
                />
                <KpiCard
                  label="Drivers"
                  value={stats.totalDrivers}
                  icon={UserCog}
                  to="/drivers"
                  breakdown={[
                    { label: 'active', value: stats.activeDrivers, tone: 'success' },
                    { label: 'suspended', value: stats.suspendedDrivers, tone: 'danger' },
                  ]}
                />
                <KpiCard
                  label="Passengers"
                  value={stats.totalUsers}
                  icon={Users}
                  to="/users"
                  breakdown={[
                    { label: 'active', value: stats.activeUsers, tone: 'success' },
                    { label: 'suspended', value: stats.suspendedUsers, tone: 'danger' },
                  ]}
                />
                <KpiCard
                  label="Promo codes"
                  value={stats.activePromoCodes}
                  icon={Ticket}
                  to="/promo-codes?state=ACTIVE"
                  breakdown={[
                    { label: 'scheduled', value: stats.scheduledPromoCodes, tone: 'warning' },
                    { label: 'expired', value: stats.expiredPromoCodes, tone: 'neutral' },
                  ]}
                />
              </>
            )}
          </section>

          <div className={styles.grid}>
            <Card>
              <CardHeader
                title="Trips created"
                description={`Daily volume over the last ${TREND_DAYS} days`}
                actions={
                  <span className={styles.trendTotal}>
                    <TrendingUp aria-hidden="true" />
                    {statsQuery.isLoading || !stats
                      ? '—'
                      : `${formatNumber(stats.tripsPerDay.reduce((sum, point) => sum + point.count, 0))} in period`}
                  </span>
                }
              />
              <CardBody>
                {statsQuery.isLoading || !stats ? (
                  <ChartFallback />
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <TrendChart data={stats.tripsPerDay} label="trips" />
                  </Suspense>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="New passengers" description={`Sign-ups over the last ${TREND_DAYS} days`} />
              <CardBody>
                {statsQuery.isLoading || !stats ? (
                  <ChartFallback />
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <TrendChart data={stats.signupsPerDay} label="sign-ups" variant="neutral" />
                  </Suspense>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Trips by status" description="Every trip on record" />
              <CardBody>
                {statsQuery.isLoading || !stats ? (
                  <Skeleton height={200} radius="var(--radius-md)" />
                ) : (
                  <StatusBreakdown
                    counts={stats.tripsByStatus}
                    linkTo={(status) => `/trips?status=${status}`}
                    emptyMessage="No trips have been created yet."
                  />
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Requests by status" description="The approval funnel" />
              <CardBody>
                {statsQuery.isLoading || !stats ? (
                  <Skeleton height={200} radius="var(--radius-md)" />
                ) : (
                  <StatusBreakdown
                    counts={stats.tripRequestsByStatus}
                    linkTo={(status) => `/trip-requests?status=${status}`}
                    emptyMessage="No trip requests have been submitted yet."
                  />
                )}
              </CardBody>
            </Card>

            <Card className={styles.wide}>
              <CardHeader
                title="Recent activity"
                description="The latest trip requests and trips across the platform"
                actions={
                  <Link to="/trips" className={styles.cardLink}>
                    View all trips
                  </Link>
                }
              />
              <CardBody className={styles.activityBody}>
                {activityQuery.isLoading ? (
                  <div className={styles.activitySkeleton}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Skeleton key={index} height={40} radius="var(--radius-sm)" />
                    ))}
                  </div>
                ) : activityQuery.isError ? (
                  <ErrorState error={activityQuery.error} onRetry={() => void activityQuery.refetch()} compact />
                ) : (activityQuery.data?.length ?? 0) === 0 ? (
                  <EmptyState
                    title="No activity yet"
                    description="Trip requests and trips will appear here as they are created."
                    compact
                  />
                ) : (
                  <ul className={styles.activityList} role="list">
                    {activityQuery.data?.map((entry) => (
                      <li key={`${entry.type}-${entry.referenceId}`}>
                        <Link
                          to={entry.type === 'TRIP' ? `/trips?search=${entry.bookingReference ?? ''}` : `/trip-requests?search=${entry.bookingReference ?? ''}`}
                          className={styles.activityRow}
                        >
                          <span className={styles.activityIcon} data-type={entry.type}>
                            {entry.type === 'TRIP' ? (
                              <CarFront aria-hidden="true" />
                            ) : (
                              <UserPlus aria-hidden="true" />
                            )}
                          </span>
                          <span className={styles.activityText}>
                            <span className={styles.activityTitle}>
                              {entry.type === 'TRIP' ? 'Trip' : 'Trip request'}{' '}
                              <span className={styles.activityRef}>{entry.bookingReference ?? `#${entry.referenceId}`}</span>
                            </span>
                            <span className={styles.activityMeta}>
                              {[entry.title, entry.subtitle].filter(Boolean).join(' · ') || 'No further details'}
                            </span>
                          </span>
                          <span className={styles.activityRight}>
                            <StatusBadge status={entry.status} />
                            <span className={styles.activityTime}>{formatRelative(entry.occurredAt)}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </>
  )
}
